import cx from 'classnames';

export interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Names the field for screen readers when there is no visible label tied to it. */
  label?: string;
  disabled?: boolean;
  /** Width of the whole control, e.g. `w-28`. */
  className?: string;
}

const Chevron = ({ up }: { up?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d={up ? 'M2 8L6 4L10 8' : 'M2 4L6 8L10 4'} />
  </svg>
);

const stepClasses =
  'flex flex-1 items-center justify-center text-ink-muted transition-colors hover:bg-surface-muted active:bg-line disabled:cursor-not-allowed disabled:text-line-strong disabled:hover:bg-transparent';

/** A whole number with −/+ steps, kept inside `min`…`max`. Height matches a `size="md"` field. */
export const NumberStepper = ({
  value,
  onChange,
  min = 1,
  max = 99,
  label,
  disabled,
  className,
}: NumberStepperProps) => {
  const set = (next: number) => onChange(Math.max(min, Math.min(max, next)));

  return (
    <div
      className={cx(
        'flex h-10 w-28 shrink-0 overflow-hidden rounded-control border border-line-strong bg-surface',
        'focus-within:border-ink focus-within:ring-1 focus-within:ring-ink',
        disabled && 'bg-surface-muted',
        className,
      )}
    >
      <input
        type="number"
        inputMode="numeric"
        aria-label={label}
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          // An emptied field keeps the last value: the control never holds "no number".
          const next = parseInt(e.target.value, 10);
          if (!Number.isNaN(next)) set(next);
        }}
        className="hide-input-number-arrow w-full min-w-0 bg-transparent text-center text-body-l text-ink outline-none disabled:cursor-not-allowed disabled:text-ink-subtle"
      />
      <div className="flex w-9 shrink-0 flex-col border-l border-line-strong">
        <button
          type="button"
          aria-label="Увеличить"
          disabled={disabled || value >= max}
          onClick={() => set(value + 1)}
          className={cx(stepClasses, 'border-b border-line-strong')}
        >
          <Chevron up />
        </button>
        <button
          type="button"
          aria-label="Уменьшить"
          disabled={disabled || value <= min}
          onClick={() => set(value - 1)}
          className={stepClasses}
        >
          <Chevron />
        </button>
      </div>
    </div>
  );
};
